import React, { useEffect, useMemo, useState } from 'react';
import { fetchChapterContents } from '../../../api/adminApi';

/** Contents whose title carries no leading number group under this label. */
const UNNUMBERED_LABEL = 'Unnumbered materials';

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

/**
 * Selects the slice of a chapter a session will teach.
 *
 * Contents arrive already in teaching order — sorted by the leading number in
 * their title, because the stored `order` column is not a usable sequence. The
 * range is contiguous and may cross a topic boundary, which is what finishing
 * one topic and opening the next looks like.
 *
 * Contents some batch in the group has already been scheduled are shown with
 * the date they were taught and cannot be picked again, so the remaining work
 * is read off the screen rather than remembered.
 *
 * The chapter may be any node of the course tree — a subject, a chapter, a
 * topic, or a JEE lecture — so contents are always requested with descendants:
 * a subject and most chapters group their children rather than holding contents
 * themselves, and would otherwise report nothing to teach.
 *
 * `mode` narrows what is offered to what that kind of session can actually be
 * linked to — a Live class is taught from a video conference, so offering it
 * the chapter's 95 videos and exams invites a link the flag logic marks
 * `missing_live` the moment it is made.
 */
const ContentRangePicker = ({ chapterId, chapterPath = [], batchIds, mode = null, selectedIds, onChange }) => {
  const [contents, setContents] = useState([]);
  const [filteredOut, setFilteredOut] = useState(0);
  const [chapter, setChapter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');

  useEffect(() => {
    if (!chapterId) {
      setContents([]);
      setFilteredOut(0);
      setChapter(null);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const data = await fetchChapterContents(chapterId, batchIds, true, mode);
        if (!active) return;
        setContents(data.contents || []);
        setFilteredOut(data.filteredOut || 0);
        setChapter(data.chapter || null);
        setRangeStart('');
        setRangeEnd('');
        onChange([]);
      } catch (loadError) {
        if (active) setError('Failed to load chapter contents.');
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => { active = false; };
    // onChange is intentionally omitted: it is recreated on every parent render
    // and would restart the fetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId, mode, JSON.stringify(batchIds)]);

  const selectable = useMemo(
    () => contents.filter((content) => !content.isScheduled),
    [contents]
  );

  const numberedOptions = useMemo(
    () => selectable.filter((content) => content.number).map((content) => content.number),
    [selectable]
  );

  const topics = useMemo(() => {
    const grouped = [];
    let current = null;
    contents.forEach((content) => {
      const key = content.number ? content.number.split('.')[0] : null;
      if (!current || current.key !== key) {
        current = { key, contents: [] };
        grouped.push(current);
      }
      current.contents.push(content);
    });

    // Head each group with the topic chapter its contents actually hang under
    // (`2. Lifestyle diseases`); "Topic 2", synthesised from the leading number,
    // named nothing a teacher would recognise. Contents that disagree, or carry
    // no topic chapter, fall back to the number.
    return grouped.map((topic) => {
      const names = new Set(
        topic.contents
          .map((content) => content.topicName)
          .filter((name) => typeof name === 'string' && name.trim() !== '')
      );
      return { ...topic, name: names.size === 1 ? [...names][0] : null };
    });
  }, [contents]);

  const selectedSet = useMemo(() => new Set(selectedIds || []), [selectedIds]);

  /** Apply a start/end pair as a contiguous run over the selectable contents. */
  const applyRange = (start, end) => {
    setRangeStart(start);
    setRangeEnd(end);
    if (!start || !end) return;

    const startIndex = selectable.findIndex((content) => content.number === start);
    const endIndex = selectable.findIndex((content) => content.number === end);
    if (startIndex === -1 || endIndex === -1 || endIndex < startIndex) {
      onChange([]);
      return;
    }
    onChange(selectable.slice(startIndex, endIndex + 1).map((content) => content.id));
  };

  const toggleOne = (content) => {
    if (content.isScheduled) return;
    const next = new Set(selectedSet);
    if (next.has(content.id)) next.delete(content.id); else next.add(content.id);
    setRangeStart('');
    setRangeEnd('');
    onChange([...next]);
  };

  const toggleTopic = (topic) => {
    const pickable = topic.contents.filter((content) => !content.isScheduled);
    if (pickable.length === 0) return;

    const allOn = pickable.every((content) => selectedSet.has(content.id));
    const next = new Set(selectedSet);
    pickable.forEach((content) => {
      if (allOn) next.delete(content.id); else next.add(content.id);
    });
    setRangeStart('');
    setRangeEnd('');
    onChange([...next]);
  };

  if (!chapterId) return <span className="cal-empty">Pick a chapter to choose its contents.</span>;
  if (loading) return <span className="cal-empty">Loading contents…</span>;
  if (error) return <div className="cal-error">{error}</div>;

  const scheduledCount = contents.length - selectable.length;

  return (
    <div className="sb-rangepicker">
      <div className="sb-panel-head">
        <h4>
          {chapterPath.length > 0 ? chapterPath.join(' › ') : (chapter ? chapter.name : 'Contents')}
        </h4>
        <span className="sb-selcount">
          {contents.length} contents · {scheduledCount} already scheduled · {selectedSet.size} selected
          {/* Say what the mode filter removed, so a chapter that is merely
              filtered does not read as an empty one. */}
          {filteredOut > 0 && ` · ${filteredOut} hidden for ${mode}`}
        </span>
      </div>

      <div className="sb-rangebar">
        <span>Range</span>
        <select
          className="cal-select sb-rangeselect"
          value={rangeStart}
          onChange={(event) => applyRange(event.target.value, rangeEnd)}
          aria-label="Range start"
        >
          <option value="">from…</option>
          {numberedOptions.map((number) => <option key={number} value={number}>{number}</option>)}
        </select>
        <span>to</span>
        <select
          className="cal-select sb-rangeselect"
          value={rangeEnd}
          onChange={(event) => applyRange(rangeStart, event.target.value)}
          aria-label="Range end"
        >
          <option value="">to…</option>
          {numberedOptions.map((number) => <option key={number} value={number}>{number}</option>)}
        </select>
        <strong>{selectedSet.size} contents</strong>
        {selectedSet.size > 0 && (
          <button type="button" className="sb-btn" onClick={() => { applyRange('', ''); onChange([]); }}>
            Clear
          </button>
        )}
      </div>

      <div className="sb-topiclist">
        {topics.map((topic) => {
          const pickable = topic.contents.filter((content) => !content.isScheduled);
          const selectedHere = topic.contents.filter((content) => selectedSet.has(content.id)).length;
          const scheduledHere = topic.contents.length - pickable.length;

          return (
            <div key={topic.key ?? 'unnumbered'} className="sb-topic">
              <button
                type="button"
                className="sb-topic-head"
                disabled={pickable.length === 0}
                aria-pressed={pickable.length > 0 && pickable.every((c) => selectedSet.has(c.id))}
                aria-label={`Select every content in ${topic.name || (topic.key ? `topic ${topic.key}` : UNNUMBERED_LABEL)}`}
                onClick={() => toggleTopic(topic)}
              >
                <input
                  type="checkbox"
                  tabIndex={-1}
                  readOnly
                  checked={pickable.length > 0 && pickable.every((c) => selectedSet.has(c.id))}
                  disabled={pickable.length === 0}
                  aria-hidden="true"
                />
                <span className="sb-topic-num">{topic.key ?? '—'}</span>
                <span className="sb-topic-name">
                  {topic.name || (topic.key ? `Topic ${topic.key}` : UNNUMBERED_LABEL)}
                </span>
                <span className="sb-topic-count">
                  {topic.contents.length} contents
                  {scheduledHere > 0 ? ` · ${scheduledHere} scheduled` : ''}
                  {selectedHere > 0 ? ` · ${selectedHere} selected` : ''}
                </span>
              </button>

              <div className="sb-topic-items">
                {topic.contents.map((content) => (
                  // A row is a button, not a <label> around the checkbox: a
                  // wrapping label forwards its own click to the input, which
                  // then toggles a second time and lands back where it started.
                  // The checkbox is decoration and takes no clicks of its own.
                  <button
                    type="button"
                    key={content.id}
                    className={`sb-citem${selectedSet.has(content.id) ? ' on' : ''}${content.isScheduled ? ' used' : ''}`}
                    disabled={content.isScheduled}
                    aria-pressed={selectedSet.has(content.id)}
                    onClick={() => toggleOne(content)}
                  >
                    <input
                      type="checkbox"
                      tabIndex={-1}
                      readOnly
                      checked={selectedSet.has(content.id)}
                      disabled={content.isScheduled}
                      aria-hidden="true"
                    />
                    <span className="sb-citem-num">{content.number || '—'}</span>
                    <span className="sb-citem-type">{content.content_type}</span>
                    <span className="sb-citem-title">{content.title}</span>
                    {content.isScheduled && (
                      <span className="sb-citem-used">{formatDate(content.scheduledOn)}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ContentRangePicker;
